import { useState, useEffect } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { AlertTriangle } from 'lucide-react';
import {
  getWards, getBeds, transferAdmission, dischargeAdmission, updateAdmissionDates,
  type Ward, type Bed as BedType,
} from '@/lib/ipdApi';
import { format } from 'date-fns';
import { StatusBadge, STATUS_LABELS, DISCHARGE_STATUSES } from './IPDAdmissionBars';

const DATETIME_LOCAL_FORMAT = "yyyy-MM-dd'T'HH:mm";

// ─── Transfer Bed Modal ───────────────────────────────────────────────────────

interface TransferModalProps {
  open: boolean;
  onClose: () => void;
  admission: any;
  onTransferred: () => void;
}

export function TransferModal({ open, onClose, admission, onTransferred }: TransferModalProps) {
  const [wards, setWards] = useState<Ward[]>([]);
  const [beds, setBeds] = useState<BedType[]>([]);
  const [wardId, setWardId] = useState('');
  const [bedId, setBedId] = useState('');
  const [notes, setNotes] = useState('');
  const [loadingBeds, setLoadingBeds] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    if (!open) return;
    setWardId('');
    setBedId('');
    setNotes('');
    setError('');
    setBeds([]);
    getWards(admission.clinic_id).then(w => setWards(w ?? [])).catch(console.error);
  }, [open, admission?.clinic_id]);

  useEffect(() => {
    if (!wardId) { setBeds([]); return; }
    setLoadingBeds(true);
    getBeds(wardId)
      .then(data => setBeds((data ?? []).filter(b => b.occupancy_status === 'available' && b.is_active)))
      .catch(() => setBeds([]))
      .finally(() => setLoadingBeds(false));
  }, [wardId]);

  const handleSubmit = async () => {
    setError('');
    if (!wardId) { setError('Select a ward'); return; }
    if (!bedId)  { setError('Select a bed'); return; }
    setSaving(true);
    try {
      await transferAdmission(admission.id, { ward_id: wardId, bed_id: bedId, notes: notes.trim() || undefined });
      onTransferred();
      onClose();
    } catch (e: any) {
      setError(e?.message || 'Failed to transfer');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-sm">
        <DialogHeader><DialogTitle>Transfer Bed</DialogTitle></DialogHeader>
        <div className="space-y-3 py-1">
          <div className="text-xs text-muted-foreground">
            Current: {admission?.ward_name} · {admission?.bed_number}
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-xs font-medium text-muted-foreground mb-1">New Ward *</label>
              <select value={wardId} onChange={e => { setWardId(e.target.value); setBedId(''); }}
                className="h-8 w-full rounded-md border border-input bg-background px-2 text-sm focus:outline-none focus:ring-1 focus:ring-ring">
                <option value="">Select ward</option>
                {wards.filter(w => w.is_active).map(w => (
                  <option key={w.id} value={w.id}>{w.name} ({w.available_beds} free)</option>
                ))}
              </select>
            </div>
            <div>
              <label className="block text-xs font-medium text-muted-foreground mb-1">New Bed *</label>
              <select value={bedId} onChange={e => setBedId(e.target.value)} disabled={!wardId || loadingBeds}
                className="h-8 w-full rounded-md border border-input bg-background px-2 text-sm focus:outline-none focus:ring-1 focus:ring-ring disabled:opacity-50">
                <option value="">{loadingBeds ? 'Loading…' : 'Select bed'}</option>
                {beds.map(b => (
                  <option key={b.id} value={b.id}>{b.bed_number}</option>
                ))}
              </select>
            </div>
          </div>
          <div>
            <label className="block text-xs font-medium text-muted-foreground mb-1">Notes</label>
            <Input value={notes} onChange={e => setNotes(e.target.value)} className="h-8 text-sm" placeholder="Reason for transfer (optional)" />
          </div>
          {error && (
            <div className="flex items-center gap-2 text-xs text-destructive bg-destructive/5 border border-destructive/20 rounded px-3 py-2">
              <AlertTriangle className="w-3.5 h-3.5 shrink-0" />{error}
            </div>
          )}
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose} disabled={saving}>Cancel</Button>
          <Button onClick={handleSubmit} disabled={saving}>{saving ? 'Transferring…' : 'Transfer'}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// ─── Discharge Modal ──────────────────────────────────────────────────────────

interface DischargeModalProps {
  open: boolean;
  onClose: () => void;
  admission: any;
  onUpdated: () => void;
}

export function DischargeModal({ open, onClose, admission, onUpdated }: DischargeModalProps) {
  const [status, setStatus] = useState('');
  const [dischargeDate, setDischargeDate] = useState('');
  const [dischargeTime, setDischargeTime] = useState('');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    if (!open) return;
    const current = admission?.status;
    setStatus(current && current !== 'admitted' ? current : 'discharged');
    const dt = admission?.discharge_date ? new Date(admission.discharge_date) : new Date();
    setDischargeDate(format(dt, 'yyyy-MM-dd'));
    setDischargeTime(format(dt, 'HH:mm'));
    setError('');
  }, [open, admission?.status, admission?.discharge_date]);

  const handleSubmit = async () => {
    setError('');
    if (!status) { setError('Select a status'); return; }
    if (!dischargeDate) { setError('Discharge date is required'); return; }
    if (!dischargeTime) { setError('Discharge time is required'); return; }

    setSaving(true);
    try {
      await dischargeAdmission(admission.id, {
        status,
        discharge_date: `${dischargeDate}T${dischargeTime}`,
      });
      onUpdated();
      onClose();
    } catch (e: any) {
      setError(e?.message || 'Failed to discharge patient');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-sm">
        <DialogHeader><DialogTitle>Discharge</DialogTitle></DialogHeader>
        <div className="space-y-3 py-1">
          <div className="text-xs text-muted-foreground">
            Current: <StatusBadge status={admission?.status ?? ''} />
          </div>
          <div>
            <label className="block text-xs font-medium text-muted-foreground mb-1">Status *</label>
            <select value={status} onChange={e => setStatus(e.target.value)}
              className="h-8 w-full rounded-md border border-input bg-background px-2 text-sm focus:outline-none focus:ring-1 focus:ring-ring">
              {DISCHARGE_STATUSES.map(s => (
                <option key={s} value={s}>{STATUS_LABELS[s] ?? s.charAt(0).toUpperCase() + s.slice(1)}</option>
              ))}
            </select>
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-xs font-medium text-muted-foreground mb-1">Discharge Date *</label>
              <Input type="date" value={dischargeDate} onChange={e => setDischargeDate(e.target.value)} className="h-8 text-sm" />
            </div>
            <div>
              <label className="block text-xs font-medium text-muted-foreground mb-1">Discharge Time *</label>
              <Input type="time" value={dischargeTime} onChange={e => setDischargeTime(e.target.value)} className="h-8 text-sm" />
            </div>
          </div>

          {admission?.status === 'admitted' && (
            <p className="text-xs text-amber-600">This will discharge the patient and free up the bed.</p>
          )}

          {error && (
            <div className="flex items-center gap-2 text-xs text-destructive bg-destructive/5 border border-destructive/20 rounded px-3 py-2">
              <AlertTriangle className="w-3.5 h-3.5 shrink-0" />{error}
            </div>
          )}
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose} disabled={saving}>Cancel</Button>
          <Button onClick={handleSubmit} disabled={saving}>{saving ? 'Saving…' : 'Discharge'}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// ─── Edit Dates Modal ─────────────────────────────────────────────────────────

interface DatesModalProps {
  open: boolean;
  onClose: () => void;
  admission: any;
  onUpdated: () => void;
}

export function DatesModal({ open, onClose, admission, onUpdated }: DatesModalProps) {
  const [admissionDate, setAdmissionDate] = useState('');
  const [dischargeDate, setDischargeDate] = useState('');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  const isTerminal = admission?.status !== 'admitted';

  useEffect(() => {
    if (!open) return;
    setError('');
    setAdmissionDate(admission?.admission_date ? format(new Date(admission.admission_date), DATETIME_LOCAL_FORMAT) : '');
    setDischargeDate(admission?.discharge_date ? format(new Date(admission.discharge_date), DATETIME_LOCAL_FORMAT) : '');
  }, [open, admission?.admission_date, admission?.discharge_date]);

  const handleSubmit = async () => {
    setError('');
    if (!admissionDate) { setError('Admission date is required'); return; }
    if (isTerminal && !dischargeDate) { setError('Discharge date is required'); return; }
    if (dischargeDate && new Date(dischargeDate) <= new Date(admissionDate)) {
      setError('Discharge date must be after admission date');
      return;
    }
    setSaving(true);
    try {
      await updateAdmissionDates(admission.id, {
        admission_date: admissionDate,
        discharge_date: isTerminal ? dischargeDate : null,
      });
      onUpdated();
      onClose();
    } catch (e: any) {
      setError(e?.message || 'Failed to update dates');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-sm">
        <DialogHeader><DialogTitle>Edit Admission Date{isTerminal ? 's' : ''}</DialogTitle></DialogHeader>
        <div className="space-y-3 py-1">
          <div>
            <label className="block text-xs font-medium text-muted-foreground mb-1">Admission Date *</label>
            <Input type="datetime-local" value={admissionDate} onChange={e => setAdmissionDate(e.target.value)} className="h-8 text-sm" />
          </div>
          {isTerminal && (
            <div>
              <label className="block text-xs font-medium text-muted-foreground mb-1">Discharge Date *</label>
              <Input type="datetime-local" value={dischargeDate} onChange={e => setDischargeDate(e.target.value)} className="h-8 text-sm" />
            </div>
          )}
          {error && (
            <div className="flex items-center gap-2 text-xs text-destructive bg-destructive/5 border border-destructive/20 rounded px-3 py-2">
              <AlertTriangle className="w-3.5 h-3.5 shrink-0" />{error}
            </div>
          )}
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose} disabled={saving}>Cancel</Button>
          <Button onClick={handleSubmit} disabled={saving}>{saving ? 'Saving…' : 'Save'}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
